import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ToggleButton } from '@repo/common';
import { Link, useLocation } from 'react-router-dom';
import { panelTo } from './panelLink';
import {
  assigneesOf,
  byJob,
  cn,
  isAssigneeOf,
  isLeader,
  isNewRequestFor,
  isNpc,
  presenceOf,
  roleLabel,
} from './model/derive';
import {
  KST,
  formatClock,
  formatDay,
  formatDue,
  formatMemberHours,
  isMemberWorking,
  tzOffsetLabel,
  tzShortName,
  zonedHourParts,
  zonedToDate,
} from './model/time';
import { dueInfo, useWorkboardCtx } from './model/useWorkboard';
import { Modal, ModalButton } from './Modal';
import TdButton from './Button';
import TdSelect from './TdSelect';

/** 크기 — 1시간 너비 · 막대 높이 · 막대 사이 · 줄 위아래 여백 · 이름 칸 너비(px). 모바일은 촘촘하게 */
const SIZES = {
  regular: { HOUR_W: 72, LANE_H: 30, LANE_GAP: 8, ROW_PAD: 14, LABEL_W: 208 },
  compact: { HOUR_W: 36, LANE_H: 24, LANE_GAP: 4, ROW_PAD: 10, LABEL_W: 104 },
};
const COMPACT_QUERY = '(max-width: 639px)';
const useCompact = () => {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(COMPACT_QUERY);
    const on = () => setCompact(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return compact;
};
const HOUR = 3_600_000;
const QUARTER = HOUR / 4;
const SNAP = 30 * 60 * 1000; // 조정은 30분 단위 (Task Flows 4b)
const MIN_SPAN = 30 * 60 * 1000;

/** 조정할 수 있는 태스크 — 승인·취소(·거절)된 태스크는 잠긴다 */
/** 조정 대상 — 제출 전 태스크만 */
// 진행 중(in_progress)인 태스크는 시작 · 마감을 옮기지 않는다 — 담당자가 이미 수락해 일하고 있다
const ADJUSTABLE = new Set(['assigned', 'rework_requested']);

/** "Leo Tran and Anonymous Otter" */
const joinNames = (names) =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** 계획 시작 — 리더가 옮겼으면 그 값, 아니면 수락(전이면 배정) 시각 */
const plannedStart = (t) => new Date(t.plannedStartAt ?? t.acceptedAt ?? t.assignedAt).getTime();

const BAR_STYLE = {
  assigned: 'bg-white border-grayscale-gray03 border-l-grayscale-gray05',
  declined: 'bg-red-50 border-red-100 border-l-red-400',
  in_progress: 'bg-orange-50 border-orange-100 border-l-orange-400',
  submitted: 'bg-purple-50 border-purple-100 border-l-purple-500',
  rework_requested: 'bg-yellow-50 border-yellow-100 border-l-yellow-400',
  approved: 'bg-green-light01 border-green-light02 border-l-green-deep02',
  cancelled: 'bg-grayscale-gray01 border-grayscale-gray02 border-l-grayscale-gray04',
};

/** 화면 시간대 기준으로 2시간 단위에 맞춘다 (눈금이 짝수 시각에 오도록) */
const snap = (date, tz, dir) => {
  const p = zonedHourParts(date, tz);
  let h = p.h - (p.h % 2);
  const base = zonedToDate(tz, p.y, p.m, p.d, h, 0);
  if (dir === 'ceil' && base.getTime() < date.getTime()) h += 2;
  return zonedToDate(tz, p.y, p.m, p.d, h, 0);
};

/** 겹치는 막대는 아래 줄로 내린다 */
const layoutBars = (bars) => {
  const laneEnds = [];
  return [...bars]
    .sort((a, b) => a.start - b.start)
    .map((b) => {
      let lane = laneEnds.findIndex((end) => end <= b.start);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = b.end;
      return { ...b, lane };
    });
};

/** 작업 시간 구간 — 30분 단위로 훑어 이어 붙인다 */
const workingRuns = (m, from, to) => {
  const runs = [];
  const step = HOUR / 2;
  let runStart = null;
  for (let t = from; t <= to; t += step) {
    const on = t < to && isMemberWorking(new Date(t), m);
    if (on && runStart === null) runStart = t;
    if (!on && runStart !== null) {
      runs.push([runStart, t]);
      runStart = null;
    }
  }
  return runs;
};

/**
 * 타임라인 — 사람별 줄에 태스크 막대(수락 시각 → 마감, 보정 기한이 있으면 그까지)와
 * 작업 시간 띠를 그린다. 시간은 고른 시간대로 보여준다.
 *
 * 평소에는 모두 보기만 한다 (막대 = 태스크 링크). 리더만 툴바의 Adjust timeline 으로 조정 모드에 들어간다 (Task Flows 4b):
 *   막대를 끌면 통째로(마감이 옮겨짐), 오른쪽 끝을 끌면 마감만 · 30분 단위 · 마지막 태스크 마감(T0+47h, 빨간 점선)까지 ·
 *   제출 전 태스크만 (제출·승인·취소된 막대는 잠김) · 담당자가 수락한 태스크는 늦추기만 ·
 *   바뀐 막대는 점선 = 이전 위치 · Save changes → 확인 → 담당자 전원에게 알림 (수락했으면 예상 제출 시각을 1회 다시 정할 수 있다)
 * Adjust timeline 은 프로젝트 시작부터 끝까지 리더에게 열려 있다 (마감은 지금 이후로만).
 * 기한 준수는 바뀐 마감으로 판정하고 따로 기록하지 않는다. 뒤 태스크 마감은 자동으로 움직이지 않는다.
 *
 * @param showEveryone false 면 담당 태스크가 있는 사람 줄만 (My tasks)
 * @param onAdjustingChange 조정 모드 켜짐/꺼짐 — 바깥(Add task 버튼)을 숨기는 데 쓴다
 */
const TimelineView = ({ tasks, showEveryone = true, onAdjustingChange }) => {
  const ctx = useWorkboardCtx();
  const location = useLocation();
  const compact = useCompact();
  const { HOUR_W, LANE_H, LANE_GAP, ROW_PAD, LABEL_W } = SIZES[compact ? 'compact' : 'regular'];
  const { data, now, displayTz, setDisplayTz, memberById } = ctx;
  const [showHours, setShowHours] = useState(true);
  const projectStartMs = new Date(data.project.startAt).getTime();
  // 리더만 · 진행 중 프로젝트 · 시작 시각부터
  const leader = !!ctx.viewer && isLeader(ctx.viewer);
  const started = now.getTime() >= projectStartMs;
  const canAdjust =
    leader && !ctx.readOnly && data.project.status !== 'ended' && data.project.status !== 'draft';
  const [adjusting, setAdjustingState] = useState(false);
  const setAdjusting = (on) => {
    setAdjustingState(on);
    onAdjustingChange?.(on);
  };
  useEffect(() => () => onAdjustingChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [edits, setEdits] = useState({}); // taskId → { ds, de } 저장 전 변화량(ms)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drag, setDrag] = useState(null); // { taskId, ds, de, x } 끄는 동안 변화량(ms) · 안내선 위치
  const dragRef = useRef(null);
  const scrollRef = useRef(null);
  const canDrag = adjusting;
  const shift = (id) => {
    const e = edits[id] ?? { ds: 0, de: 0 };
    const live = drag?.taskId === id ? drag : null;
    return { ds: e.ds + (live?.ds ?? 0), de: e.de + (live?.de ?? 0) };
  };

  const rangeStart = snap(new Date(data.project.startAt), displayTz, 'floor').getTime();
  const rangeEnd = snap(new Date(data.project.endAt), displayTz, 'ceil').getTime();
  const hours = Math.round((rangeEnd - rangeStart) / HOUR);
  const width = hours * HOUR_W;
  const x = (t) => ((t - rangeStart) / HOUR) * HOUR_W;

  // 팀원 + 이탈자 (이탈자도 줄은 남긴다). 팀장은 담당 태스크가 없으니 뺀다
  const rows = [...memberById.values()]
    .filter((m) => !isNpc(m))
    .filter((m) => showEveryone || tasks.some((t) => isAssigneeOf(t, m.id)))
    .sort(byJob);

  const barsByMember = new Map(
    rows.map((m) => [
      m.id,
      layoutBars(
        tasks
          .filter((t) => isAssigneeOf(t, m.id))
          .map((t) => ({
            task: t,
            // 수락 전이면 배정 시각부터 — 수락 대기 중이라는 게 보인다
            start: plannedStart(t) + shift(t.id).ds,
            // 평소처럼 보정 기한까지 그리고, 조정 중이면 변화량을 더한다
            end: dueInfo(ctx, t).effective.getTime() + shift(t.id).de,
            // 조정 전 위치 (바뀐 막대의 점선 테두리)
            start0: plannedStart(t),
            end0: dueInfo(ctx, t).effective.getTime(),
          })),
      ),
    ]),
  );

  // 시간대 — KST 와 내 현지 시간 두 가지 (Task Flows W1 Timeline · KST / Local time)
  const myTz = ctx.viewer?.timezone;
  const zones = useMemo(() => [...new Set([KST, myTz].filter(Boolean))], [myTz]);

  // 처음 열 때(·시간대를 바꿀 때) Now 선이 왼쪽 1/3 쯤 오게
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(0, x(now.getTime()) - (el.clientWidth - LABEL_W) / 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTz, compact]);

  const projectEndMs = new Date(data.project.endAt).getTime();
  const snapMs = (t) => Math.round(t / SNAP) * SNAP;
  // 마지막 태스크 마감 — 조정은 T0+47h 까지 (빨간 점선)
  const lastDueMs = Math.min(projectEndMs, projectStartMs + 47 * HOUR);

  /**
   * mode: move(막대 통째로) · start(왼쪽 끝 — 시작만) · end(오른쪽 끝 — 마감만).
   * 30분 단위 · 최소 30분 · 마감은 지금 이후 ~ 마지막 태스크 마감 · 시작은 프로젝트 시작 이후 (1014 핸드오프 §8)
   */
  const beginDrag = (e, task, mode, start, end) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const prev = edits[task.id] ?? { ds: 0, de: 0 };
    const due0 = new Date(task.dueAt).getTime() + prev.de;
    const start0 = plannedStart(task) + prev.ds;
    const d = { x0: e.clientX, moved: false, ds: 0, de: 0 };
    dragRef.current = d;

    const onMove = (ev) => {
      const dx = ev.clientX - d.x0;
      if (!d.moved && Math.abs(dx) < 4) return;
      d.moved = true;
      const delta = snapMs((dx / HOUR_W) * HOUR);
      let ds = 0;
      let de = 0;
      if (mode === 'start') {
        // 시작 — 프로젝트 시작 이후, 마감 30분 전까지
        const s1 = Math.min(
          Math.max(start0 + delta, projectStartMs),
          Math.min(due0, end) - MIN_SPAN,
        );
        ds = s1 - start0;
      } else {
        // 마감은 지금 이후 ~ 마지막 태스크 마감 안 · 수락한 태스크는 원래 마감보다 앞당기지 않는다
        const floor = Math.max(now.getTime(), task.acceptedAt ? new Date(task.dueAt).getTime() : 0);
        const clamped = Math.min(Math.max(due0 + delta, floor), Math.max(lastDueMs, due0));
        de = clamped - due0;
        // 끝만 끌 때는 최소 30분은 남긴다
        if (mode === 'end') de = Math.max(de, start + MIN_SPAN - end);
        if (mode === 'move') ds = de;
      }
      d.ds = ds;
      d.de = de;
      const at = mode === 'start' ? start + ds : end + de;
      setDrag({ taskId: task.id, ds, de, mode, at, x: x(at) });
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dragRef.current = null;
      setDrag(null);
      if (!d.moved || (!d.ds && !d.de)) return;
      setEdits((cur) => {
        const next = { ...cur, [task.id]: { ds: prev.ds + d.ds, de: prev.de + d.de } };
        if (!next[task.id].ds && !next[task.id].de) delete next[task.id];
        return next;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // 저장할 변경 — 시작·마감이 실제로 바뀐 것만
  const changes = Object.entries(edits)
    .map(([id, e]) => {
      const task = tasks.find((t) => t.id === id);
      if (!task || (!e.de && !e.ds)) return null;
      return {
        task,
        assignee: memberById.get(task.assigneeId),
        assignees: assigneesOf(task)
          .map((id) => memberById.get(id))
          .filter(Boolean),
        from: new Date(task.dueAt),
        to: new Date(new Date(task.dueAt).getTime() + e.de),
        startFrom: new Date(plannedStart(task)),
        startAt: new Date(plannedStart(task) + e.ds),
      };
    })
    .filter(Boolean);
  /** "3h 30m" */
  const spanLabel = (ms) => {
    const m = Math.round(ms / 60000);
    return m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`;
  };
  const changedCount = changes.length;
  const tzLabel = tzShortName(displayTz, now);

  const exitAdjust = () => {
    setEdits({});
    setConfirmOpen(false);
    setAdjusting(false);
  };
  const save = () => {
    if (!changes.length) return;
    ctx.dispatch({
      type: 'adjustSchedule',
      changes: changes.map((c) => ({
        taskId: c.task.id,
        startAt: c.startAt.toISOString(),
        dueAt: c.to.toISOString(),
      })),
      at: now.toISOString(),
    });
    const names = [...new Set(changes.flatMap((c) => c.assignees.map((m) => m.name)))];
    ctx.toast?.({
      title: 'Timeline saved',
      sub: `${joinNames(names)} get${names.length === 1 ? 's' : ''} notified`,
    });
    exitAdjust();
  };

  const ticks = Array.from({ length: Math.floor(hours / 2) + 1 }, (_, i) => {
    const t = rangeStart + i * 2 * HOUR;
    return { t, label: formatClock(new Date(t), displayTz) };
  });
  const midnights = [];
  for (let t = rangeStart + QUARTER; t < rangeEnd; t += QUARTER) {
    if (formatClock(new Date(t), displayTz) === '00:00') midnights.push(t);
  }
  const dateChips = [rangeStart, ...midnights];
  const dividers = midnights.map((t) => (
    <span
      key={`d-${t}`}
      aria-hidden
      style={{ left: x(t) }}
      className='pointer-events-none absolute inset-y-0 border-l-[1.5px] border-dotted border-green-deep03/40'
    />
  ));

  const nowX = x(now.getTime());
  const nowInRange = now.getTime() >= rangeStart && now.getTime() <= rangeEnd;

  const lastDueX = x(lastDueMs);

  return (
    <div>
      {adjusting ? (
        // 조정 모드 상단 바 — Cancel / Save changes (바뀐 게 있어야 켜진다)
        <div
          role='region'
          aria-label='Adjusting timeline'
          className='mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-surface-green10 px-4 py-3'
        >
          <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
            <span className='flex flex-wrap items-center gap-2 text-[15px] font-medium text-text-primary'>
              Adjusting timeline
              <span className='rounded-full bg-grayscale-gray00 px-2 py-0.5 text-[12px] font-medium text-green-deep04 shadow-[inset_0_0_0_1px_#1dc37b]'>
                Moves in 30-min steps
              </span>
            </span>
            <span className='text-[13px] text-text-secondary'>
              {changedCount > 0
                ? `${changedCount} task${changedCount === 1 ? '' : 's'} changed · dashed outline = before`
                : 'Drag a bar to move it, or drag either edge to change its length · until the last task due · tasks sent back for rework can only move later · in-progress, submitted, approved and cancelled tasks are locked'}
            </span>
          </div>
          <div className='flex gap-2'>
            <ModalButton onClick={exitAdjust}>Cancel</ModalButton>
            <ModalButton primary disabled={!changedCount} onClick={() => setConfirmOpen(true)}>
              Save changes
            </ModalButton>
          </div>
        </div>
      ) : (
        <div className='mb-4 flex flex-wrap items-center gap-x-6 gap-y-2'>
          <label className='flex min-w-0 items-center gap-3 text-[13px] text-text-secondary sm:text-[14px]'>
            Time zone
            <TdSelect
              aria-label='Time zone'
              value={displayTz}
              onChange={(e) => setDisplayTz(e.target.value)}
              className='h-9 min-w-0 flex-1 rounded-xl border border-grayscale-gray03 bg-white px-3 text-[13px] text-text-primary focus:border-green-deep02 focus:outline-none sm:h-10 sm:min-w-[260px] sm:text-[14px]'
            >
              {zones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz === KST
                    ? `KST · Korea (${tzOffsetLabel(tz, now)})`
                    : `Local · ${ctx.viewer?.name ?? ''} · ${tz.split('/').pop()?.replace(/_/g, ' ')} (${tzOffsetLabel(tz, now)})`}
                </option>
              ))}
            </TdSelect>
          </label>
          {/* 공용 ToggleButton은 div 클릭만 받아서 — 키보드·스크린리더용 switch 껍데기를 씌운다 */}
          <div
            role='switch'
            aria-checked={showHours}
            aria-label='Working hours'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key !== ' ' && e.key !== 'Enter') return;
              e.preventDefault();
              setShowHours(!showHours);
            }}
            className='rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-deep02'
          >
            <ToggleButton
              content='Working hours'
              value={showHours}
              onChange={setShowHours}
              size='s'
            />
          </div>
          {canAdjust && (
            <TdButton
              variant='soft'
              size='s'
              disabled={!started}
              title={
                !started
                  ? `Available from ${formatClock(new Date(projectStartMs), KST)} KST`
                  : undefined
              }
              onClick={() => setAdjusting(true)}
              className='ml-auto'
            >
              Adjust timeline
            </TdButton>
          )}
        </div>
      )}

      <div
        ref={scrollRef}
        className='overflow-x-auto rounded-2xl border border-grayscale-gray02 bg-white'
      >
        <div style={{ width: LABEL_W + width }} className='relative'>
          {/* 머리줄 */}
          <div className='flex border-b border-grayscale-gray02'>
            <div
              style={{ width: LABEL_W }}
              className='sticky left-0 z-20 flex shrink-0 items-center border-r border-grayscale-gray02 bg-white px-3 py-3 text-[12px] text-text-secondary sm:px-5 sm:py-4 sm:text-[14px]'
            >
              {formatDay(new Date(rangeStart), displayTz)} –{' '}
              {formatDay(new Date(rangeEnd - 1), displayTz)}
            </div>
            <div className='relative h-[76px]' style={{ width }}>
              {ticks.map((tk) => (
                <span
                  key={tk.t}
                  style={{ left: x(tk.t) }}
                  className='absolute bottom-0 top-7 whitespace-nowrap border-l border-green-deep03/[0.08] pl-1.5 pt-1.5 text-[11px] text-text-secondary'
                >
                  {tk.label}
                </span>
              ))}
              {dividers}
              {dateChips.map((t) => (
                <span
                  key={t}
                  style={{ left: x(t) }}
                  className='absolute top-1 z-[2] ml-1.5 flex h-5 items-center whitespace-nowrap rounded-full bg-surface-green10 px-2 text-[12px] font-medium leading-none text-green-deep04'
                >
                  {formatDay(new Date(t), displayTz)}
                </span>
              ))}
              {nowInRange && (
                <span
                  style={{ left: nowX }}
                  className='absolute bottom-1 z-10 -translate-x-1/2 rounded-md bg-green-deep02 px-1.5 py-0.5 text-[11px] font-medium text-white'
                >
                  Now {formatClock(now, displayTz)}
                </span>
              )}
            </div>
          </div>

          {/* 사람별 줄 */}
          {rows.map((m) => {
            const bars = barsByMember.get(m.id) ?? [];
            const lanes = Math.max(1, ...bars.map((b) => b.lane + 1));
            const height = ROW_PAD * 2 + lanes * LANE_H + (lanes - 1) * LANE_GAP;
            const presence = presenceOf(m, now);
            return (
              <div key={m.id} className='flex border-b border-grayscale-gray02 last:border-b-0'>
                <div
                  style={{ width: LABEL_W, minHeight: height }}
                  className='sticky left-0 z-20 flex shrink-0 flex-col justify-center border-r border-grayscale-gray02 bg-white px-3 py-2 sm:px-5 sm:py-3'
                >
                  <span className='text-[13px] font-medium leading-[140%] text-text-primary sm:text-[14px]'>
                    {m.name}
                  </span>
                  <span className='text-[11px] leading-[140%] text-text-secondary sm:text-[12px]'>
                    {roleLabel(m)} ·{' '}
                    {presence === 'dropped' ? 'Dropped' : formatMemberHours(m, displayTz, now)}
                    {/* 모바일은 이름 칸이 좁아 한 문장으로 잇는다 */}
                    {presence === 'offline' && compact && ' · off hours now'}
                  </span>
                  {presence === 'offline' && !compact && (
                    <span className='text-[12px] text-text-placeholder'>off hours now</span>
                  )}
                </div>
                <div className='relative' style={{ width, height }}>
                  {showHours &&
                    presence !== 'dropped' &&
                    workingRuns(m, rangeStart, rangeEnd).map(([a, b]) => (
                      <span
                        key={a}
                        aria-hidden
                        style={{ left: x(a), width: x(b) - x(a) }}
                        className='absolute inset-y-0 bg-green-light01/70'
                      />
                    ))}
                  {dividers}
                  {bars.map((b) => {
                    const left = Math.max(0, x(b.start));
                    const right = Math.min(width, x(b.end));
                    const isNew = isNewRequestFor(b.task, data.viewerId);
                    const editable = canDrag && ADJUSTABLE.has(b.task.state);
                    const locked = adjusting && !ADJUSTABLE.has(b.task.state);
                    const dragging = drag?.taskId === b.task.id;
                    const changed = !!edits[b.task.id];
                    const style = {
                      left,
                      width: Math.max(right - left, 24),
                      top: ROW_PAD + b.lane * (LANE_H + LANE_GAP),
                      height: LANE_H,
                    };
                    const cls = cn(
                      'group absolute z-[1] flex items-center rounded-lg [overflow:clip] border border-l-[3px] px-2 text-[12px] text-text-primary transition-shadow hover:shadow-md sm:text-[13px]',
                      BAR_STYLE[b.task.state],
                      isNew && 'border-green-deep02 border-l-green-deep02',
                      editable && 'cursor-grab touch-none select-none active:cursor-grabbing',
                      locked && 'cursor-not-allowed opacity-50',
                      changed && !dragging && 'ring-2 ring-green-deep02',
                      dragging &&
                        'z-[5] -translate-y-0.5 cursor-grabbing shadow-[0_8px_20px_rgba(0,0,0,0.18)] ring-2 ring-green-deep02',
                    );
                    // 바뀐 막대 — 이전 위치를 점선으로
                    const ghost = (changed || dragging) && (
                      <span
                        key={`${b.task.id}-before`}
                        aria-hidden
                        style={{
                          left: Math.max(0, x(b.start0)),
                          width: Math.max(
                            Math.min(width, x(b.end0)) - Math.max(0, x(b.start0)),
                            24,
                          ),
                          top: style.top,
                          height: LANE_H,
                        }}
                        className='pointer-events-none absolute z-0 rounded-lg border border-dashed border-green-deep03/50'
                      />
                    );
                    // 태스크명은 가로로 스크롤해도 이름 칸 바로 옆에 붙어 있다 (막대 안에서만 — 막대가 지나가면 같이 빠진다).
                    // 막대는 overflow-hidden 대신 clip 이어야 sticky 가 바깥 스크롤을 따른다
                    const body = (
                      <span
                        style={{ left: LABEL_W + 8 }}
                        className='sticky flex min-w-0 items-center gap-1.5'
                      >
                        {b.task.isBlocked && (
                          <span
                            className='h-1.5 w-1.5 shrink-0 rounded-full bg-red-500'
                            aria-label='Blocked'
                          />
                        )}
                        <span className='truncate'>{b.task.title}</span>
                      </span>
                    );
                    if (!adjusting) {
                      return (
                        <Link
                          key={b.task.id}
                          to={panelTo(location, b.task.id)}
                          title={b.task.title}
                          style={style}
                          className={cls}
                        >
                          {body}
                        </Link>
                      );
                    }
                    if (!editable) {
                      return (
                        <div
                          key={b.task.id}
                          title={`${b.task.title} · ${b.task.state === 'in_progress' ? 'in progress, can’t move' : 'locked'}`}
                          style={style}
                          className={cls}
                        >
                          {body}
                        </div>
                      );
                    }
                    const dueLabel = `${formatDay(new Date(b.end), displayTz)} ${formatClock(new Date(b.end), displayTz)}`;
                    return (
                      <React.Fragment key={b.task.id}>
                        {ghost}
                        <div
                          role='button'
                          tabIndex={0}
                          aria-label={`${b.task.title} · due ${dueLabel} · drag to change`}
                          title={`${b.task.title} · due ${dueLabel}`}
                          style={style}
                          className={cls}
                          onPointerDown={(e) => beginDrag(e, b.task, 'move', b.start, b.end)}
                        >
                          {body}
                        </div>
                        {/* 양쪽 끝 손잡이 — 손잡이 6×18, 누르는 영역 28×44 (막대 밖 좌우 12px · 위아래 8px) */}
                        {[
                          ['start', 'Change start', style.left],
                          ['end', 'Change due', style.left + style.width],
                        ].map(([m, label, cx]) => (
                          <span
                            key={m}
                            role='slider'
                            aria-label={`${label} · ${b.task.title}`}
                            aria-valuetext={
                              m === 'start'
                                ? formatDue(new Date(b.start), displayTz)
                                : formatDue(new Date(b.end), displayTz)
                            }
                            onPointerDown={(e) => beginDrag(e, b.task, m, b.start, b.end)}
                            style={{ left: cx - 14, top: style.top + LANE_H / 2 - 22 }}
                            className='group/handle absolute z-[6] flex h-11 w-7 cursor-ew-resize touch-none items-center justify-center'
                          >
                            <span className='h-[18px] w-1.5 rounded-full bg-green-deep02/60 shadow-[0_0_0_2px_#ffffff] group-hover/handle:bg-green-deep02' />
                          </span>
                        ))}
                        {/* 끄는 동안 칩 — "Due 18:00 → 19:30 · 3h 30m" */}
                        {dragging && (
                          <span
                            style={{
                              left: drag.mode === 'start' ? style.left : style.left + style.width,
                              top: Math.max(0, style.top - 26),
                            }}
                            className={cn(
                              'pointer-events-none absolute z-[7] whitespace-nowrap rounded-md bg-text-primary px-2 py-0.5 text-[11px] font-medium text-grayscale-gray00',
                              drag.mode === 'start' ? '' : '-translate-x-full',
                            )}
                          >
                            {drag.mode === 'start'
                              ? `Start ${formatClock(new Date(b.start0), displayTz)} → ${formatClock(new Date(b.start), displayTz)}`
                              : `Due ${formatClock(new Date(b.end0), displayTz)} → ${formatClock(new Date(b.end), displayTz)}`}{' '}
                            · {spanLabel(b.end - b.start)}
                          </span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* 조정 모드 — 마지막 태스크 마감(여기까지 옮길 수 있다) */}
          {adjusting && lastDueMs >= rangeStart && lastDueMs <= rangeEnd && (
            <>
              <span
                aria-hidden
                style={{ left: LABEL_W + lastDueX }}
                className='pointer-events-none absolute bottom-0 top-[76px] z-10 border-l-2 border-dashed border-functional-failed03'
              />
              <span
                style={{ left: LABEL_W + lastDueX }}
                className='pointer-events-none absolute top-[54px] z-10 -translate-x-1/2 whitespace-nowrap rounded-md bg-functional-failed01 px-1.5 py-0.5 text-[11px] font-medium text-functional-failed04'
              >
                Last task due {formatClock(new Date(lastDueMs), displayTz)}
              </span>
            </>
          )}
          {/* 끄는 동안 안내선 + 지금 놓으면 되는 시각 (30분 단위로 붙는다) */}
          {drag && (
            <>
              <span
                aria-hidden
                style={{ left: LABEL_W + drag.x }}
                className='pointer-events-none absolute bottom-0 top-[76px] z-10 w-px bg-green-deep02'
              />
              <span
                style={{ left: LABEL_W + drag.x }}
                className='pointer-events-none absolute top-[52px] z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-green-deep04 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-grayscale-gray00'
              >
                {drag.mode === 'start' ? 'Start' : 'Due'} {formatDay(new Date(drag.at), displayTz)}{' '}
                {formatClock(new Date(drag.at), displayTz)}
              </span>
            </>
          )}

          {/* Now 선 — 머리줄 아래 전체 */}
          {nowInRange && (
            <span
              aria-hidden
              style={{ left: LABEL_W + nowX }}
              className='pointer-events-none absolute bottom-0 top-[76px] z-10 w-0.5 -translate-x-1/2 bg-green-deep02'
            />
          )}
        </div>
      </div>

      {confirmOpen && (
        <Modal
          open
          title='Save timeline changes?'
          subtitle={`${changedCount} task${changedCount === 1 ? '' : 's'} · every assignee gets a notification. If they already accepted, they can reset their expected submit time once. Later tasks keep their due times.`}
          onClose={() => setConfirmOpen(false)}
          width='max-w-[520px]'
          footer={
            <>
              <ModalButton onClick={() => setConfirmOpen(false)}>Keep adjusting</ModalButton>
              <ModalButton primary onClick={save}>
                Save changes
              </ModalButton>
            </>
          }
        >
          <ul className='flex flex-col gap-3'>
            {changes.map((c) => (
              <li key={c.task.id} className='flex flex-col gap-0.5'>
                <span className='text-[13px] font-medium text-text-primary'>
                  {c.task.title}
                  {c.assignees.length ? ` · ${c.assignees.map((m) => m.name).join(', ')}` : ''}
                </span>
                {/* "Start … → … · Due … → … KST · 4h" (바뀐 쪽만) */}
                <span className='text-[14px] text-text-secondary'>
                  {[
                    c.startAt.getTime() !== c.startFrom.getTime() &&
                      `Start ${formatDue(c.startFrom, displayTz)} → ${formatDue(c.startAt, displayTz)}`,
                    c.to.getTime() !== c.from.getTime() &&
                      `Due ${formatDue(c.from, displayTz)} → ${formatDue(c.to, displayTz)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}{' '}
                  {tzLabel} · {spanLabel(c.to - c.startAt)}
                </span>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
};

export default TimelineView;
